import React, { forwardRef, HTMLAttributes } from 'react'
import styled, { css } from 'styled-components'
import { useSegmentTabsContext } from './SegmentTabs.provider'
import Text from '@/components/content/Text'
import { colorSystem } from '@/styles/theme'

interface SegmentTabItemProps extends HTMLAttributes<HTMLDivElement> {
  label: string
  value: string
  disabled?: boolean
  isFirst?: boolean
  isLast?: boolean
}

export const SegmentTabItem = forwardRef<HTMLDivElement, SegmentTabItemProps>(
  ({ label, value, disabled, isFirst = false, isLast = false, ...htmlDivProps }, ref) => {
    const { selectValue, onTabChange } = useSegmentTabsContext()

    const isSelected = selectValue === value

    const handleClick = (value: string) => {
      if (!disabled) onTabChange(value)
    }

    return (
      <SegmentTabItemWrap
        role="tab"
        aria-selected={isSelected}
        onClick={() => handleClick(value)}
        ref={ref}
        disabled={disabled}
        $isSelected={isSelected}
        $isFirst={isFirst}
        $isLast={isLast}
        {...htmlDivProps}
      >
        <Text
          kind={isSelected ? 'Body_14_medium' : 'Body_14_regular'}
          style={{ transition: 'all 0.3s' }}
          color={isSelected ? colorSystem.text.common.primary : disabled ? colorSystem.text.common.disabled : '#8a8a8a'}
        >
          {label}
        </Text>
      </SegmentTabItemWrap>
    )
  },
)

const SegmentTabItemWrap = styled.div<{
  disabled?: boolean
  $isSelected: boolean
  $isFirst: boolean
  $isLast: boolean
}>`
  flex: 1;
  min-width: 64px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 36px;
  background-color: white;
  border-top: 1px solid ${({ $isSelected }) => ($isSelected ? colorSystem.text.common.primary : '#e0e0e0')};
  border-right: 1px solid ${({ $isSelected }) => ($isSelected ? colorSystem.text.common.primary : '#e0e0e0')};
  border-bottom: 1px solid ${({ $isSelected }) => ($isSelected ? colorSystem.text.common.primary : '#e0e0e0')};
  border-left: ${({ $isFirst, $isSelected }) => ($isFirst ? `1px solid ${$isSelected ? colorSystem.text.common.primary : '#e0e0e0'}` : 'none')};
  border-radius: ${({ $isFirst, $isLast }) => {
    if ($isFirst) return '4px 0 0 4px'
    if ($isLast) return '0 4px 4px 0'
    return '0'
  }};
  transition: all 0.3s;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      background-color: ${colorSystem.bg.input.disabled};
    `}

  &:not([aria-selected='true']):hover {
    opacity: 0.8;
  }

  &:has(+ [aria-selected='true']) {
    border-right: 1px solid ${colorSystem.text.common.primary};
    cursor: pointer;
  }
`
